import type { Dispatch } from "react";
import type { EditorAction } from "../editor/reducers";
import type { TableState } from "./types";
import { Accordion } from "@acdc2/ui/components/accordion";
import { TableEditorStep1 } from "./TableEditorStep1";
import { TableEditorStep2 } from "./TableEditorStep2";
import { TableEditorStep3 } from "./TableEditorStep3";
import { TableEditorStep4 } from "./TableEditorStep4";
import { TableEditorStep5 } from "./TableEditorStep5";
import { TableEditorStep6 } from "./TableEditorStep6";
import { TableEditorStep7 } from "./TableEditorStep7";
import {
  checkBottomRowIsSelected,
  checkSelectedCellsAreAtomic,
  checkSelectionIsFullHeight,
  checkSelectionIsFullWidth,
  checkSelectionIsRectangular,
  checkSelectionSpansSingleCol,
  checkSelectionSpansSingleRow,
  checkSingleCellIsSelected,
  checkTopRowIsSelected,
  getCommonHeadersOfSelectedCells,
  getSoleRoleOfSelectedCells,
  getSoleScopeOfSelectedCells,
  getSoleSelectedGroup,
} from "./calculations";
import type { TableEditorFragment } from "../client/generated";

type Props = {
  dispatch: Dispatch<EditorAction>;
  fragment: TableEditorFragment;
  tableState: TableState;
};

export function TableEditor({ tableState, fragment, dispatch }: Props) {
  const isSelectionRectangular = checkSelectionIsRectangular(tableState.cells);
  const isSingleCellSelected = checkSingleCellIsSelected(tableState.cells);
  const isSelectionAtomic = checkSelectedCellsAreAtomic(tableState.cells);
  const isHeaderless = tableState.cells.every((cell) => cell.role !== "th");
  const isAnyCellSelected = tableState.cells.some((cell) => cell.selected);
  const isTopRowSelected = checkTopRowIsSelected(tableState.cells);
  const isBottomRowSelected = checkBottomRowIsSelected(
    tableState.cells,
    tableState.rows,
  );
  const isSelectionFullWidth = checkSelectionIsFullWidth(
    tableState.cells,
    tableState.cols,
  );
  const isSelectionFullHeight = checkSelectionIsFullHeight(
    tableState.cells,
    tableState.rows,
  );
  const isSelectionSpanningSingleRow = checkSelectionSpansSingleRow(
    tableState.cells,
  );
  const isSelectionSpanningSingleCol = checkSelectionSpansSingleCol(
    tableState.cells,
  );

  const soleCellRole = getSoleRoleOfSelectedCells(tableState.cells);
  const soleCellScope = getSoleScopeOfSelectedCells(tableState.cells);
  const soleTableGroup = getSoleSelectedGroup(
    tableState.cells,
    tableState.rows,
  );
  const commonHeaders = getCommonHeadersOfSelectedCells(tableState.cells);

  return (
    <Accordion type="single" collapsible>
      <TableEditorStep1
        dispatch={dispatch}
        fragment={fragment.page}
        tableState={tableState}
      />
      <TableEditorStep2
        dispatch={dispatch}
        isSelectionFullHeight={isSelectionFullHeight}
        isSelectionFullWidth={isSelectionFullWidth}
        isSelectionSpanningSingleCol={isSelectionSpanningSingleCol}
      />
      <TableEditorStep3
        dispatch={dispatch}
        isSelectionFullHeight={isSelectionFullHeight}
        isSelectionFullWidth={isSelectionFullWidth}
        isSelectionSpanningSingleRow={isSelectionSpanningSingleRow}
      />
      <TableEditorStep4
        dispatch={dispatch}
        soleTableGroup={soleTableGroup}
        isSelectionAtomic={isSelectionAtomic}
        isSingleCellSelected={isSingleCellSelected}
        isSelectionRectangular={isSelectionRectangular}
      />
      <TableEditorStep5
        dispatch={dispatch}
        soleCellRole={soleCellRole}
        soleCellScope={soleCellScope}
        isAnyCellSelected={isAnyCellSelected}
      />
      <TableEditorStep6
        fragment={fragment.page}
        dispatch={dispatch}
        tableState={tableState}
        commonHeaders={commonHeaders}
        isHeaderless={isHeaderless}
        isAnyCellSelected={isAnyCellSelected}
      />
      <TableEditorStep7
        dispatch={dispatch}
        soleTableGroup={soleTableGroup}
        isSelectionRectangular={isSelectionRectangular}
        isSelectionFullWidth={isSelectionFullWidth}
        isTopRowSelected={isTopRowSelected}
        isBottomRowSelected={isBottomRowSelected}
      />
    </Accordion>
  );
}
